import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GroupsContext } from '../../context/group/GroupContext';
import Message from '../../components/ui/Message';
import { User } from '../../models/interfaces';

interface LocationState {
  group: {
    group_id: number;
    group_name: string;
  };
}

export const GroupUsersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { group } = location.state as LocationState;

  const { loadUsers, loadGroupUsers, saveGroupUsers } =
    useContext(GroupsContext);

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  /* =========================
     Cargar usuarios
  ========================== */

  useEffect(() => {
    setLoading(true);

    Promise.all([
      loadUsers(),
      loadGroupUsers(group.group_id)
    ])
      .then(([allUsers, groupUsers]) => {
        setUsers(allUsers);
        setSelectedUsers(groupUsers.map(u => u.user_id));
      })
      .finally(() => setLoading(false));
  }, []);

  /* =========================
     Toggle usuario
  ========================== */

  const handleToggleUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  /* =========================
     Guardar
  ========================== */

  const handleSave = async () => {
    const payload = {
      group_id: group.group_id,
      users: selectedUsers
    };

    const resp = await saveGroupUsers(payload);

    if (resp === 'success') {
      setSuccessMessage('Usuarios asignados correctamente.');
    } else {
      setErrorMessage('Ocurrió un error asignando los usuarios.');
    }
  };

  const removeError = () => setErrorMessage('');
  const removeSuccess = () => {
    setSuccessMessage('');
    navigate(-1);
  };

  /* =========================
     Render
  ========================== */

  return (
    <div className="mt-5 container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5>
          Usuarios del grupo:{' '}
          <span className="fw-bold">{group.group_name}</span>
        </h5>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center mt-4">
          <span className="spinner-border" />
        </div>
      ) : (
        <table className="table table-hover table-striped">
          <thead className="thead-dark">
            <tr>
              <th style={{ width: 120, textAlign: 'center' }}>
                Asignado
              </th>
              <th>Usuario</th>
              <th>Nombre</th>
              <th>Email</th>
            </tr>
          </thead>

          <tbody>
            {users.length > 0 ? (
              users.map(user => (
                <tr key={user.user_id}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.user_id)}
                      onChange={() => handleToggleUser(user.user_id)}
                    />
                  </td>

                  <td>{user.user_name}</td>

                  <td>
                    {user.name} {user.lastname}
                  </td>

                  <td className="text-muted">{user.email}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center">
                  No existen usuarios.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Acciones */}
      <div className="d-flex justify-content-center mt-4">
        <button
          className="btn btn-outline-secondary me-5"
          onClick={() => navigate(-1)}
        >
          Volver
        </button>

        <button
          className="btn btn-success"
          onClick={handleSave}
        >
          Guardar cambios
        </button>
      </div>

      {/* Mensajes */}
      {errorMessage && (
        <Message
          type="error"
          message={errorMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}

      {successMessage && (
        <Message
          type="success"
          title="Éxito"
          message={successMessage}
          closeDialog={removeSuccess}
          handleOk={removeSuccess}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}
    </div>
  );
};
